import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import {
  getDefaultSellerDestination,
  isBuyerOnboardingComplete,
  isSellerOnboardingComplete,
  isSellerRole,
  SELLER_ONBOARDING_PATH,
  BUYER_ONBOARDING_PATH,
} from "../features/auth/onboarding";
import { parseApiError } from "../lib/http/apiErrors";
import { authService } from "../features/auth/service";
import type { SignupInitResponse } from "../features/auth/types";
import PhoneInputField from "../components/ui/PhoneInputField";
import InfoTip from "../components/ui/InfoTip";
import { useGoogleLogin } from "@react-oauth/google";
import AppleSignInButton from "../features/auth/AppleSignInButton";

// ── Types ──────────────────────────────────────────────────────────────────

interface LoginForm  { identifier: string; password: string }
interface SignupForm { name: string; email: string; role: "buyer" | "seller"; phone: string; password: string }
type Step = "form" | "otp";

// ── Component ──────────────────────────────────────────────────────────────

const LoginPage: React.FC = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { login, signup, loginWithSession, isAuthenticated, isAuthReady, user } = useAuth();

  const [isLogin,      setIsLogin]      = useState(
    (location.state as { mode?: string } | null)?.mode !== "signup",
  );
  const [step,         setStep]         = useState<Step>("form");
  const [pendingSignup, setPendingSignup] = useState<SignupInitResponse | null>(null);
  const [error,        setError]        = useState("");
  const [isLoading,        setIsLoading]        = useState(false);
  const [showPassword,     setShowPassword]     = useState(false);
  const [pendingGoogleToken, setPendingGoogleToken] = useState<string | null>(null);

  const [loginForm, setLoginForm] = useState<LoginForm>({ identifier: "", password: "" });
  const [signupForm, setSignupForm] = useState<SignupForm>({
    name: "", email: "", role: "buyer", phone: "", password: "",
  });

  // ── Redirect if already authenticated ─────────────────────────────────

  useEffect(() => {
    if (!isAuthReady || !isAuthenticated || !user) return;
    if (step === "otp") return; // stay on OTP step
    const from = (location.state as { from?: string } | null)?.from;
    _redirect(user, from ?? null, navigate);
  }, [isAuthReady, isAuthenticated, user, location.state, navigate, step]);

  // ── Submit form ────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      if (isLogin) {
        const sessionUser = await login(loginForm.identifier, loginForm.password);
        const from = (location.state as { from?: string } | null)?.from;
        _redirect(sessionUser, from ?? null, navigate);
      } else {
        const result = await signup(
          signupForm.name,
          signupForm.phone,
          signupForm.role,
          signupForm.email,
          signupForm.password,
        );
        // Account not created yet — show OTP screen
        setPendingSignup(result);
        setStep("otp");
      }
    } catch (err) {
      setError(parseApiError(err, "Authentication failed. Please try again.").message);
    } finally {
      setIsLoading(false);
    }
  };

  // ── OTP verified ───────────────────────────────────────────────────────

  const handleGoogleAuth = async (idToken: string, role?: "buyer" | "seller") => {
    setError("");
    setIsLoading(true);
    try {
      const resolvedRole = role ?? (isLogin ? undefined : signupForm.role);
      const session = await authService.googleSignin(idToken, resolvedRole);
      loginWithSession(session);
      const from = (location.state as { from?: string } | null)?.from;
      _redirect(session.user, from ?? null, navigate);
    } catch (err: any) {
      const detail = err?.response?.data?.detail ?? "";
      if (detail === "role_required") {
        setPendingGoogleToken(idToken);
      } else {
        setError(parseApiError(err, "Google sign-in failed. Try again.").message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const triggerGoogleLogin = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      // Google's implicit flow with openid scope returns id_token (JWT) alongside access_token.
      // The @react-oauth/google types don't expose id_token but it is present in the actual response.
      const idToken = (tokenResponse as unknown as { id_token?: string }).id_token;
      if (idToken) {
        handleGoogleAuth(idToken);
      } else {
        setError("Google sign-in failed. Try again.");
      }
    },
    onError: () => setError("Google sign-in failed. Try again."),
    flow: "implicit",
    scope: "openid email profile",
  });

  const handleAppleAuth = async (identityToken: string, fullName?: string) => {
    setError("");
    setIsLoading(true);
    try {
      const session = await authService.appleSignin(identityToken, {
        role: isLogin ? undefined : signupForm.role,
        userName: fullName,
      });
      loginWithSession(session);
      const from = (location.state as { from?: string } | null)?.from;
      _redirect(session.user, from ?? null, navigate);
    } catch (err) {
      console.error("[AppleSignIn] backend rejected identity token:", err);
      setError(parseApiError(err, "Apple sign-in failed. Try again.").message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpVerified = (session: { accessToken: string; user: any }) => {
    loginWithSession(session);
    const from = (location.state as { from?: string } | null)?.from;
    _redirect(session.user, from ?? null, navigate);
  };

  // ── Render ─────────────────────────────────────────────────────────────

  if (pendingGoogleToken) {
    return (
      <RolePickerDialog
        onPick={(role) => {
          const token = pendingGoogleToken;
          setPendingGoogleToken(null);
          handleGoogleAuth(token, role);
        }}
      />
    );
  }

  if (step === "otp" && pendingSignup) {
    return (
      <OtpStep
        pending={pendingSignup}
        onVerified={handleOtpVerified}
        onBack={() => { setStep("form"); setIsLogin(false); setError(""); }}
      />
    );
  }

  return (
    <div className="page-shell flex min-h-screen items-center justify-center bg-gradient-to-br from-surface-subtle via-surface to-brand-muted/30 px-4 py-10">
      <div className="w-full max-w-md animate-fade-in-up">

        {/* Logo */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand to-accent text-xl font-bold text-white shadow-glow">G</div>
          <h1 className="text-3xl font-bold tracking-tight text-ink">Gullivr</h1>
          <p className="mt-2 text-sm text-ink-muted">AI-powered travel marketplace</p>
        </div>

        <div className="glass-panel p-6 sm:p-8">
          {/* Tab toggle */}
          <div className="segmented mb-8 w-full">
            <button type="button" onClick={() => { setIsLogin(true); setError(""); }} className={isLogin ? "segmented-item-active flex-1" : "segmented-item flex-1"}>Sign in</button>
            <button type="button" onClick={() => { setIsLogin(false); setError(""); }} className={!isLogin ? "segmented-item-active flex-1" : "segmented-item flex-1"}>Create account</button>
          </div>

          {error && <div className="alert-error mb-6">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-5">
            {isLogin ? (
              <LoginFields form={loginForm} setForm={setLoginForm} showPassword={showPassword} setShowPassword={setShowPassword} />
            ) : (
              <SignupFields form={signupForm} setForm={setSignupForm} showPassword={showPassword} setShowPassword={setShowPassword} />
            )}

            <button type="submit" disabled={isLoading} className="btn-primary btn-lg w-full">
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="spinner-sm border-white/30 border-t-white" aria-hidden="true" />
                  {isLogin ? "Signing in…" : "Creating account…"}
                </span>
              ) : isLogin ? "Sign in" : "Create account"}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-surface px-3 text-ink-muted">or continue with</span>
            </div>
          </div>

          {/* Google Sign-In — native button triggers Google OAuth2 popup */}
          <button
            type="button"
            onClick={() => triggerGoogleLogin()}
            disabled={isLoading}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-border bg-white px-4 py-2.5 text-sm font-medium text-[#3c4043] transition-colors hover:bg-gray-50 disabled:opacity-50"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.35-8.16 2.35-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              <path fill="none" d="M0 0h48v48H0z"/>
            </svg>
            {isLogin ? "Sign in with Google" : "Sign up with Google"}
          </button>

          {/* Apple Sign-In (renders only when VITE_APPLE_SERVICE_ID + redirect URI are configured) */}
          <div className="mt-3">
            <AppleSignInButton
              mode={isLogin ? "signin" : "signup"}
              disabled={isLoading}
              onSuccess={handleAppleAuth}
              onError={(msg) => setError(msg)}
            />
          </div>

          <div className="mt-6 text-center">
            <button type="button" onClick={() => navigate("/")} className="text-sm font-medium text-ink-muted hover:text-ink">← Explore as guest</button>
          </div>
        </div>

        <p className="mt-6 text-center text-sm text-ink-muted">
          {isLogin ? "Don't have an account? " : "Already have an account? "}
          <button type="button" onClick={() => { setIsLogin(!isLogin); setError(""); }} className="font-semibold text-brand hover:text-brand-hover">
            {isLogin ? "Sign up" : "Sign in"}
          </button>
        </p>
      </div>
    </div>
  );
};

// ── Sub-components ─────────────────────────────────────────────────────────

/** Red asterisk marking a required field. */
const RequiredStar: React.FC = () => (
  <span className="text-red-500 ml-0.5" aria-hidden="true">*</span>
);

const PasswordToggle: React.FC<{ show: boolean; onToggle: () => void }> = ({ show, onToggle }) => (
  <button type="button" onClick={onToggle} aria-label={show ? "Hide password" : "Show password"}
    className="btn-icon absolute inset-y-0 right-1 my-auto h-8 w-8 border-0 bg-transparent shadow-none">
    {show ? (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18M10.584 10.587a2 2 0 002.828 2.828M9.88 5.09A9.956 9.956 0 0112 4c5 0 9 4 10 8a17.847 17.847 0 01-3.293 4.568M6.228 6.228A17.863 17.863 0 002 12c1 4 5 8 10 8a9.954 9.954 0 004.091-.88" /></svg>
    ) : (
      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
    )}
  </button>
);

const LoginFields: React.FC<{
  form: LoginForm;
  setForm: (f: LoginForm) => void;
  showPassword: boolean;
  setShowPassword: (v: boolean) => void;
}> = ({ form, setForm, showPassword, setShowPassword }) => {
  const [usePhone, setUsePhone] = useState(false);

  const handlePhoneChange = (e164: string) => {
    setForm({ ...form, identifier: e164 });
  };

  return (
    <>
      <div>
        {/* Email / Phone toggle */}
        <div className="flex items-center justify-between mb-1">
          <label className="label" htmlFor="login-id">{usePhone ? "WhatsApp number" : "Email address"}</label>
          <button
            type="button"
            onClick={() => {
              setUsePhone((p) => !p);
              setForm({ ...form, identifier: "" });
            }}
            className="text-xs text-primary hover:underline font-medium"
          >
            Use {usePhone ? "email" : "WhatsApp number"} instead
          </button>
        </div>

        {usePhone ? (
          <PhoneInputField
            id="login-id"
            label=""
            value={form.identifier}
            onChange={handlePhoneChange}
            required
            defaultCountryIso2="IN"
            placeholder="WhatsApp number"
          />
        ) : (
          <input
            id="login-id"
            type="email"
            value={form.identifier}
            onChange={(e) => setForm({ ...form, identifier: e.target.value })}
            required
            className="input input-lg w-full"
            placeholder="you@company.com"
            autoComplete="email"
          />
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="label" htmlFor="login-pw">Password</label>
          <a href="/forgot-password" className="text-xs font-medium text-brand hover:text-brand-hover">
            Forgot password?
          </a>
        </div>
        <div className="relative">
          <input id="login-pw" type={showPassword ? "text" : "password"} value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            required minLength={6} className="input input-lg pr-12" placeholder="••••••••" autoComplete="current-password" />
          <PasswordToggle show={showPassword} onToggle={() => setShowPassword(!showPassword)} />
        </div>
      </div>
    </>
  );
};

const SignupFields: React.FC<{
  form: SignupForm;
  setForm: (f: SignupForm) => void;
  showPassword: boolean;
  setShowPassword: (v: boolean) => void;
}> = ({ form, setForm, showPassword, setShowPassword }) => (
  <>
    {/* Name */}
    <div>
      <label className="label" htmlFor="su-name">Full name<RequiredStar /></label>
      <input id="su-name" type="text" value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        required className="input input-lg" placeholder="Your full name" autoComplete="name" />
    </div>

    {/* Phone — mandatory, always gets a WhatsApp OTP */}
    <div>
      <PhoneInputField
        id="su-phone"
        label="WhatsApp number"
        value={form.phone}
        onChange={(e164) => setForm({ ...form, phone: e164 })}
        required
        defaultCountryIso2="IN"
        placeholder="WhatsApp number"
      />
    </div>

    {/* Email — optional, verified separately later from the profile page */}
    <div>
      <label className="label" htmlFor="su-email">Email</label>
      <input id="su-email" type="email" value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
        className="input input-lg" placeholder="you@company.com" autoComplete="email" />
    </div>

    {/* Password — set once, at signup only. Enables password login as a
        fallback alongside phone/OTP. */}
    <div>
      <label className="label" htmlFor="su-pw">Password<RequiredStar /></label>
      <div className="relative">
        <input id="su-pw" type={showPassword ? "text" : "password"} value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          required minLength={8} className="input input-lg pr-12" placeholder="Min 8 characters" autoComplete="new-password" />
        <PasswordToggle show={showPassword} onToggle={() => setShowPassword(!showPassword)} />
      </div>
    </div>

    {/* Role */}
    <div>
      <div className="flex items-center gap-1">
        <label className="label" htmlFor="su-role">Account type<RequiredStar /></label>
        <InfoTip message="Buyers browse deals. Sellers (agencies, hotels, operators) list travel products." />
      </div>
      <select id="su-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as "buyer" | "seller" })} className="select input-lg w-full">
        <option value="buyer">Buyer — I'm looking for travel deals</option>
        <option value="seller">Seller — I'm a travel business / agency</option>
      </select>
    </div>
  </>
);

// ── OTP Step ───────────────────────────────────────────────────────────────

const OtpStep: React.FC<{
  pending: SignupInitResponse;
  onVerified: (session: { accessToken: string; user: any }) => void;
  onBack: () => void;
}> = ({ pending, onVerified, onBack }) => {
  const { phone } = pending;
  const [digits, setDigits]     = useState<string[]>(Array(4).fill(""));
  const [error, setError]       = useState("");
  const [isLoading, setLoading] = useState(false);
  const [resent, setResent]     = useState(false);
  const [resendCooldown, setResendCooldown] = useState(pending.expires_in_seconds ?? 60);
  const inputRefs               = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, [resendCooldown > 0]);

  const otp = digits.join("");

  const handleChange = (idx: number, val: string) => {
    const char = val.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[idx] = char;
    setDigits(next);
    if (char && idx < 3) inputRefs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    if (pasted.length === 4) {
      setDigits(pasted.split(""));
      inputRefs.current[3]?.focus();
    }
  };

  const handleVerify = async () => {
    if (otp.length < 4) { setError("Enter all 4 digits"); return; }
    setError("");
    setLoading(true);
    try {
      const session = await authService.verifySignupOtp(phone, otp);
      onVerified(session);
    } catch (err) {
      setError(parseApiError(err, "Invalid or expired code. Try again.").message);
      setDigits(Array(4).fill(""));
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setResent(false);
    setError("");
    try {
      const { expires_in_seconds } = await authService.resendSignupOtp(phone);
      setResent(true);
      setResendCooldown(expires_in_seconds ?? 60);
      setDigits(Array(4).fill(""));
      inputRefs.current[0]?.focus();
    } catch {
      setError("Could not resend code. Try again.");
    }
  };

  return (
    <div className="page-shell flex min-h-screen items-center justify-center bg-gradient-to-br from-surface-subtle via-surface to-brand-muted/30 px-4 py-10">
      <div className="w-full max-w-md animate-fade-in-up">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand to-accent text-xl font-bold text-white shadow-glow">G</div>
          <h1 className="text-3xl font-bold tracking-tight text-ink">Verify your number</h1>
          <p className="mt-2 text-sm text-ink-muted">
            We sent a 4-digit code on WhatsApp to <span className="font-semibold text-ink">{phone}</span>
          </p>
        </div>

        <div className="glass-panel p-6 sm:p-8">
          {error   && <div className="alert-error mb-6">{error}</div>}
          {resent  && <div className="alert-success mb-6">New code sent — check WhatsApp.</div>}
          {!pending.phone_otp && (
            <div className="alert-success mb-6">Dev mode: phone OTP verification is off — enter 0000.</div>
          )}

          {/* OTP boxes */}
          <div className="flex justify-center gap-3 mb-8" onPaste={handlePaste}>
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => { inputRefs.current[i] = el; }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={d}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                className="h-14 w-12 rounded-xl border-2 border-border bg-surface text-center text-2xl font-bold text-ink shadow-sm outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/20"
                aria-label={`Digit ${i + 1}`}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={handleVerify}
            disabled={isLoading || otp.length < 4}
            className="btn-primary btn-lg w-full"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="spinner-sm border-white/30 border-t-white" aria-hidden="true" />
                Verifying…
              </span>
            ) : "Verify & create account"}
          </button>

          <div className="mt-5 flex items-center justify-between text-sm">
            <button type="button" onClick={onBack} className="font-medium text-ink-muted hover:text-ink">
              ← Back
            </button>
            <button
              type="button"
              onClick={handleResend}
              disabled={resendCooldown > 0}
              className="font-medium text-brand hover:text-brand-hover disabled:cursor-not-allowed disabled:text-ink-muted disabled:hover:text-ink-muted"
            >
              {resendCooldown > 0
                ? `Resend code in 0:${String(resendCooldown).padStart(2, "0")}`
                : "Resend code"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Role Picker Dialog ─────────────────────────────────────────────────────

const RolePickerDialog: React.FC<{ onPick: (role: "buyer" | "seller") => void }> = ({ onPick }) => {
  useBodyScrollLock();
  return (
  <div className="page-shell fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
    <div className="w-full max-w-sm animate-fade-in-up rounded-2xl bg-surface p-8 shadow-xl">
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand to-accent text-xl font-bold text-white shadow-glow">G</div>
      <h2 className="text-center text-xl font-bold text-ink">One quick thing</h2>
      <p className="mt-2 text-center text-sm text-ink-muted">How will you be using Gullivr?</p>
      <div className="mt-8 flex flex-col gap-3">
        <button
          type="button"
          onClick={() => onPick("buyer")}
          className="flex flex-col items-start gap-1 rounded-xl border-2 border-border bg-surface-subtle px-5 py-4 text-left transition hover:border-brand hover:bg-brand/5"
        >
          <span className="font-semibold text-ink">Buyer</span>
          <span className="text-xs text-ink-muted">I'm looking for travel deals &amp; experiences</span>
        </button>
        <button
          type="button"
          onClick={() => onPick("seller")}
          className="flex flex-col items-start gap-1 rounded-xl border-2 border-border bg-surface-subtle px-5 py-4 text-left transition hover:border-brand hover:bg-brand/5"
        >
          <span className="font-semibold text-ink">Seller</span>
          <span className="text-xs text-ink-muted">I'm a travel agency, hotel, or operator</span>
        </button>
      </div>
    </div>
  </div>
  );
};

// ── Navigation helper ──────────────────────────────────────────────────────

function _redirect(user: any, from: string | null, navigate: (path: string, opts?: any) => void) {
  if (user.role === "super_admin") {
    navigate(from || "/");
    return;
  }
  if (isSellerRole(user.role)) {
    const next = from || getDefaultSellerDestination();
    if (!isSellerOnboardingComplete(user)) {
      navigate(SELLER_ONBOARDING_PATH, { state: { from: next } });
    } else {
      navigate(next);
    }
  } else {
    const next = from || "/";
    if (!isBuyerOnboardingComplete(user)) {
      navigate(BUYER_ONBOARDING_PATH, { state: { from: next } });
    } else {
      navigate(next);
    }
  }
}

export default LoginPage;
